import React, { useEffect, useRef } from 'react';
import { FormEditor } from '@bpmn-io/form-js';
import '@bpmn-io/form-js/dist/assets/form-js-base.css';
import '@bpmn-io/form-js/dist/assets/form-js.css';
import '@bpmn-io/form-js/dist/assets/form-js-editor-base.css';
import '@bpmn-io/form-js/dist/assets/form-js-editor.css';
import '@bpmn-io/form-js/dist/assets/properties-panel.css';
import './FormBuilderEditor.css';

const DEFAULT_FORM_SCHEMA = {
  type: 'default',
  components: [],
};

const parseSchema = (rawContent) => {
  if (!rawContent || rawContent === 'undefined' || rawContent === 'null') {
    return DEFAULT_FORM_SCHEMA;
  }

  if (typeof rawContent === 'object' && Array.isArray(rawContent.components)) {
    return rawContent;
  }

  try {
    const parsed = JSON.parse(rawContent);
    if (parsed && Array.isArray(parsed.components)) {
      return parsed;
    }
  } catch (error) {
    // ignore and fallback
  }

  return DEFAULT_FORM_SCHEMA;
};

const FormBuilderEditor = ({ schemaContent, onSchemaChange }) => {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const isImportingRef = useRef(false);
  const lastSerializedSchemaRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const editor = new FormEditor({
      container: containerRef.current,
    });
    editorRef.current = editor;

    const emitSchema = async () => {
      if (!onSchemaChange || isImportingRef.current) {
        return;
      }

      const schema = await editor.saveSchema();
      const serialized = JSON.stringify(schema);
      lastSerializedSchemaRef.current = serialized;
      onSchemaChange(serialized);
    };

    editor.on('changed', emitSchema);

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, [onSchemaChange]);

  useEffect(() => {
    const importSchema = async () => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      const raw = schemaContent || '';
      if (lastSerializedSchemaRef.current !== null && raw === lastSerializedSchemaRef.current) {
        return;
      }

      try {
        isImportingRef.current = true;
        const schema = parseSchema(raw);
        await editor.importSchema(schema);
        lastSerializedSchemaRef.current = raw;
      } finally {
        isImportingRef.current = false;
      }
    };

    importSchema().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error importing form schema', error);
    });
  }, [schemaContent]);

  return <div className="form-builder-canvas" ref={containerRef} />;
};

export default FormBuilderEditor;
